import api from './api.service';

export type AffiliateCompany = {
  company_id: string;
  company_name: string;
  company_code: string;
  link_id: string;
  vat_rate?: number;
  notes?: string | null;
};

export type TenantCompany = {
  id: string;
  name: string;
  code: string;
  same_tenant?: boolean;
};

export type IcTransaction = {
  id: string;
  ic_number: string;
  transaction_date: string;
  transaction_type: string;
  status: string;
  seller_company_id: string;
  buyer_company_id: string;
  seller_company_name?: string;
  seller_company_code?: string;
  buyer_company_name?: string;
  buyer_company_code?: string;
  seller_warehouse_id?: string | null;
  buyer_warehouse_id?: string | null;
  seller_warehouse_name?: string;
  seller_warehouse_code?: string;
  buyer_warehouse_name?: string;
  buyer_warehouse_code?: string;
  transfer_price_method: string;
  currency: string;
  subtotal: number;
  vat_amount: number;
  grand_total: number;
  seller_journal_voucher_id?: string;
  buyer_journal_voucher_id?: string;
  seller_journal_voucher_number?: string | null;
  buyer_journal_voucher_number?: string | null;
  seller_sales_invoice_id?: string;
  buyer_supplier_invoice_id?: string;
  seller_sales_invoice_number?: string | null;
  buyer_supplier_invoice_number?: string | null;
  approval_instance_id?: string;
  notes?: string;
  lines?: IcTransactionLine[];
  posted_at?: string;
};

export type IcTransactionLine = {
  id?: string;
  line_number: number;
  seller_item_id?: string | null;
  buyer_item_id?: string | null;
  item_code?: string;
  item_description: string;
  quantity: number;
  uom?: string;
  unit_price: number;
  unit_cost?: number | null;
  unrealized_unit_markup?: number | null;
  line_total: number;
  notes?: string;
};

export type IcBalanceRow = {
  affiliate_company_id: string;
  affiliate_company_name: string;
  due_from_balance: number;
  due_to_balance: number;
  net_balance: number;
};

export type IcSettlement = {
  id: string;
  settlement_number: string;
  settlement_date: string;
  settlement_type: string;
  status: string;
  company_id: string;
  counterparty_company_id: string;
  currency: string;
  due_from_before: number;
  due_to_before: number;
  netted_amount: number;
  notes?: string;
  posted_at?: string;
};

export type IcStatement = {
  affiliate_company_id: string;
  affiliate_company_name: string;
  as_of?: string;
  due_from_balance: number;
  due_to_balance: number;
  net_balance: number;
  aging: { current: number; d1_30: number; d31_60: number; d61_90: number; d90_plus: number };
  lines: Array<{
    id: string;
    document_number: string;
    document_type: string;
    transaction_type: string;
    transaction_date: string;
    direction: 'DueFrom' | 'DueTo';
    amount: number;
    days_outstanding: number;
    status: string;
  }>;
};

export type AffiliateWarehouse = {
  id: string;
  warehouse_code: string;
  warehouse_name: string;
};

export type AffiliateItem = {
  id: string;
  item_code: string;
  item_name: string;
  base_uom?: string;
  standard_cost?: number;
};

export type ConsolidationGroup = {
  id: string;
  group_code: string;
  group_name: string;
  parent_company_id: string;
  reporting_currency: string;
  is_active: boolean;
  notes?: string;
};

export type ConsolidationGroupMember = {
  id: string;
  member_company_id: string;
  ownership_pct: number;
  consolidation_method: string;
  is_parent: boolean;
  sort_order: number;
  company_code?: string;
  company_name?: string;
};

export type ConsolidationGroupDetail = ConsolidationGroup & {
  members: ConsolidationGroupMember[];
};

export type EliminationLine = {
  elimination_type: 'IC_AR_AP' | 'IC_REVENUE_EXPENSE' | 'IC_UNREALIZED_INVENTORY';
  description: string;
  debit_account_code: string;
  credit_account_code: string;
  amount: number;
};

export type EliminationWorksheet = {
  group_id: string;
  group_code: string;
  group_name: string;
  as_of_date: string;
  from_date: string | null;
  reporting_currency: string;
  lines: EliminationLine[];
  total_eliminations: number;
  members: Array<{
    member_company_id: string;
    company_code: string;
    company_name: string;
    ownership_pct: number;
    consolidation_method: string;
    factor: number;
  }>;
};

export type ConsolidatedTrialBalance = {
  group_id: string;
  group_code: string;
  group_name: string;
  as_of_date: string;
  reporting_currency: string;
  rows: Array<{
    account_code: string;
    account_name: string;
    account_type: string;
    combined_debit: number;
    combined_credit: number;
    elimination_debit: number;
    elimination_credit: number;
    consolidated_debit: number;
    consolidated_credit: number;
  }>;
  eliminations: EliminationLine[];
  total_combined_debit: number;
  total_combined_credit: number;
  total_consolidated_debit: number;
  total_consolidated_credit: number;
  is_balanced: boolean;
};

export type ConsolidatedProfitLoss = {
  group_id: string;
  group_code: string;
  group_name: string;
  from_date: string;
  to_date: string;
  reporting_currency: string;
  revenue: { section_name: string; accounts: Array<{ account_code: string; account_name: string; amount: number }>; subtotal: number };
  expenses: { section_name: string; accounts: Array<{ account_code: string; account_name: string; amount: number }>; subtotal: number };
  net_profit: number;
  eliminations: EliminationLine[];
  ic_revenue_expense_eliminated: number;
};

export type ConsolidatedBalanceSheet = {
  group_id: string;
  group_code: string;
  group_name: string;
  as_of_date: string;
  reporting_currency: string;
  assets: { section_name: string; accounts: Array<{ account_code: string; account_name: string; amount: number }>; subtotal: number };
  liabilities: { section_name: string; accounts: Array<{ account_code: string; account_name: string; amount: number }>; subtotal: number };
  equity: { section_name: string; accounts: Array<{ account_code: string; account_name: string; amount: number }>; subtotal: number };
  retained_earnings: number;
  total_assets: number;
  total_liabilities: number;
  total_equity: number;
  total_liabilities_and_equity: number;
  is_balanced: boolean;
  eliminations: EliminationLine[];
};

const intercompanyService = {
  listAffiliates: async (): Promise<AffiliateCompany[]> => {
    const res = await api.get<AffiliateCompany[]>('/intercompany/affiliates');
    return res.data ?? [];
  },

  listTenantCompanies: async (): Promise<TenantCompany[]> => {
    const res = await api.get<TenantCompany[]>('/intercompany/tenant-companies');
    return res.data ?? [];
  },

  createAffiliate: async (affiliate_company_id: string, notes?: string) => {
    const res = await api.post('/intercompany/affiliates', { affiliate_company_id, notes });
    return res.data;
  },

  updateAffiliate: async (
    linkId: string,
    payload: { vat_rate?: number; notes?: string | null; is_active?: boolean }
  ) => {
    const res = await api.put(`/intercompany/affiliates/${linkId}`, payload);
    return res.data;
  },

  listTransactions: async (params?: {
    status?: string;
    buyer_company_id?: string;
    transaction_type?: string;
  }): Promise<IcTransaction[]> => {
    const res = await api.get<IcTransaction[]>('/intercompany/transactions', { params });
    return res.data ?? [];
  },

  getTransaction: async (id: string): Promise<IcTransaction> => {
    const res = await api.get<IcTransaction>(`/intercompany/transactions/${id}`);
    return res.data as IcTransaction;
  },

  createTransaction: async (payload: Record<string, unknown>): Promise<IcTransaction> => {
    const res = await api.post<IcTransaction>('/intercompany/transactions', payload);
    return res.data as IcTransaction;
  },

  updateTransaction: async (id: string, payload: Record<string, unknown>): Promise<IcTransaction> => {
    const res = await api.put<IcTransaction>(`/intercompany/transactions/${id}`, payload);
    return res.data as IcTransaction;
  },

  submitTransaction: async (id: string): Promise<IcTransaction> => {
    const res = await api.post<IcTransaction>(`/intercompany/transactions/${id}/submit`);
    return res.data as IcTransaction;
  },

  approveTransaction: async (id: string, remarks?: string): Promise<IcTransaction> => {
    const res = await api.post<IcTransaction>(`/intercompany/transactions/${id}/approve`, {
      remarks,
    });
    return res.data as IcTransaction;
  },

  rejectTransaction: async (
    id: string,
    payload: { rejection_reason_code?: string; remarks?: string }
  ): Promise<IcTransaction> => {
    const res = await api.post<IcTransaction>(`/intercompany/transactions/${id}/reject`, payload);
    return res.data as IcTransaction;
  },

  postTransaction: async (
    id: string,
    body?: {
      allocate_to_buyer_project_id?: string;
      buyer_wip_warehouse_id?: string;
    }
  ): Promise<
    IcTransaction & {
      allocate_to_project_wip?: { stock_transfer?: { id?: string }; message?: string };
      allocate_to_project_wip_error?: string;
    }
  > => {
    const res = await api.post(`/intercompany/transactions/${id}/post`, body || {});
    const bodyRes = res.data as Record<string, unknown> | undefined;
    const nested =
      bodyRes && typeof bodyRes === 'object' && 'data' in bodyRes
        ? (bodyRes.data as Record<string, unknown>)
        : bodyRes;
    return (nested || bodyRes) as IcTransaction;
  },

  cancelTransaction: async (id: string): Promise<IcTransaction> => {
    const res = await api.post<IcTransaction>(`/intercompany/transactions/${id}/cancel`);
    return res.data as IcTransaction;
  },

  deleteTransaction: async (id: string): Promise<{ id: string; deleted: boolean }> => {
    const res = await api.delete<{ id: string; deleted: boolean }>(`/intercompany/transactions/${id}`);
    return res.data as { id: string; deleted: boolean };
  },

  balanceReport: async (asOf?: string): Promise<IcBalanceRow[]> => {
    const res = await api.get<IcBalanceRow[]>('/intercompany/balance-report', { params: asOf ? { as_of: asOf } : undefined });
    return res.data ?? [];
  },

  statement: async (affiliateCompanyId: string, asOf?: string): Promise<IcStatement> => {
    const res = await api.get<IcStatement>(`/intercompany/statement/${affiliateCompanyId}`, { params: asOf ? { as_of: asOf } : undefined });
    return res.data as IcStatement;
  },

  listSettlements: async (): Promise<IcSettlement[]> => {
    const res = await api.get<IcSettlement[]>('/intercompany/settlements');
    return res.data ?? [];
  },

  getSettlement: async (id: string): Promise<IcSettlement> => {
    const res = await api.get<IcSettlement>(`/intercompany/settlements/${id}`);
    return res.data as IcSettlement;
  },

  createSettlement: async (payload: Record<string, unknown>): Promise<IcSettlement> => {
    const res = await api.post<IcSettlement>('/intercompany/settlements', payload);
    return res.data as IcSettlement;
  },

  postSettlement: async (id: string): Promise<IcSettlement> => {
    const res = await api.post<IcSettlement>(`/intercompany/settlements/${id}/post`);
    return res.data as IcSettlement;
  },

  deleteSettlement: async (id: string): Promise<{ id: string; deleted: boolean }> => {
    const res = await api.delete<{ id: string; deleted: boolean }>(`/intercompany/settlements/${id}`);
    return res.data as { id: string; deleted: boolean };
  },

  affiliateWarehouses: async (affiliateCompanyId: string): Promise<AffiliateWarehouse[]> => {
    const res = await api.get<AffiliateWarehouse[]>(
      `/intercompany/affiliates/${affiliateCompanyId}/warehouses`
    );
    return res.data ?? [];
  },

  affiliateItems: async (affiliateCompanyId: string): Promise<AffiliateItem[]> => {
    const res = await api.get<AffiliateItem[]>(`/intercompany/affiliates/${affiliateCompanyId}/items`);
    return res.data ?? [];
  },

  listGroups: async (): Promise<ConsolidationGroup[]> => {
    const res = await api.get<ConsolidationGroup[]>('/intercompany/groups');
    return res.data ?? [];
  },

  getGroup: async (id: string): Promise<ConsolidationGroupDetail> => {
    const res = await api.get<ConsolidationGroupDetail>(`/intercompany/groups/${id}`);
    return res.data as ConsolidationGroupDetail;
  },

  createGroup: async (payload: Record<string, unknown>): Promise<ConsolidationGroupDetail> => {
    const res = await api.post<ConsolidationGroupDetail>('/intercompany/groups', payload);
    return res.data as ConsolidationGroupDetail;
  },

  updateGroup: async (id: string, payload: Record<string, unknown>): Promise<ConsolidationGroupDetail> => {
    const res = await api.put<ConsolidationGroupDetail>(`/intercompany/groups/${id}`, payload);
    return res.data as ConsolidationGroupDetail;
  },

  eliminationWorksheet: async (
    groupId: string,
    params: { as_of_date: string; from_date?: string }
  ): Promise<EliminationWorksheet> => {
    const res = await api.get<EliminationWorksheet>(
      `/intercompany/groups/${groupId}/elimination-worksheet`,
      { params }
    );
    return res.data as EliminationWorksheet;
  },

  consolidatedTrialBalance: async (
    groupId: string,
    params: { as_of_date: string; from_date?: string }
  ): Promise<ConsolidatedTrialBalance> => {
    const res = await api.get<ConsolidatedTrialBalance>(
      `/intercompany/groups/${groupId}/consolidated-trial-balance`,
      { params }
    );
    return res.data as ConsolidatedTrialBalance;
  },

  consolidatedProfitLoss: async (
    groupId: string,
    params: { from_date: string; to_date: string }
  ): Promise<ConsolidatedProfitLoss> => {
    const res = await api.get<ConsolidatedProfitLoss>(
      `/intercompany/groups/${groupId}/consolidated-profit-loss`,
      { params }
    );
    return res.data as ConsolidatedProfitLoss;
  },

  consolidatedBalanceSheet: async (
    groupId: string,
    params: { as_of_date: string }
  ): Promise<ConsolidatedBalanceSheet> => {
    const res = await api.get<ConsolidatedBalanceSheet>(
      `/intercompany/groups/${groupId}/consolidated-balance-sheet`,
      { params }
    );
    return res.data as ConsolidatedBalanceSheet;
  },
};

export default intercompanyService;
